import type { FeedingPen, FeedingPenOption } from './penTypes';
import type {
    FeedingDashboardOverviewResponse,
    FeedingPenResponse,
    FeedingRefreshStatusResponse,
    FeedingSeriesPointResponse,
} from './responseTypes';
import type { FeedingOverlaySources, FeedingSeriesPoint } from './seriesTypes';
import { mapFeedingSummaryToDomain } from './summaryFallback';
import type { BioscopeDataQuality, FeedingDashboardOverview, FeedingRefreshStatus } from './types';

/**
 * Tolerates an absent `feeding`: a rolling deploy can land a request on a replica
 * running the previous version, whose response omits the field entirely. That reads
 * as "not refreshing" rather than crashing the dashboard or the poller.
 */
function mapFeedingRefreshStatusToDomain(
    response: FeedingRefreshStatusResponse | undefined
): FeedingRefreshStatus {
    return {
        refreshing: response?.refreshing ?? false,
        staleCageIds: response?.staleCageIds ?? [],
        lastFetchedAt: response?.lastFetchedAt ? new Date(response.lastFetchedAt) : null,
    };
}

/**
 * What a previous backend, which sends no overlay sources, is known to have done. It
 * filled temperature and saturation from the planning system on any day the Bioscope had
 * no reading, so the legend must not present them as Bioscope values; its mg/L and
 * salinity came from the Bioscope sensors alone. Remove with the series fallback below.
 */
const PREVIOUS_BACKEND_OVERLAY_SOURCES: FeedingOverlaySources = {
    temperatureC: 'unknown',
    oxygenMgL: 'bioscope',
    oxygenSaturationPct: 'unknown',
    salinityPpt: 'bioscope',
};

/**
 * A previous backend sends one estimate series and one trend series for all pens, with
 * no split by readiness. Rebuilding the chart it drew keeps a rolling deploy from
 * blanking the Bioscope weight: while the selection was ready it drew the trend, else the
 * estimates as points. The current backend still sends that pair, deprecated, beside the
 * split fields, and the split fields win. Remove once no replica runs that version.
 */
function mapSeriesPointToDomain(
    point: FeedingSeriesPointResponse,
    selectionQuality: BioscopeDataQuality
): FeedingSeriesPoint {
    const {
        weightBioscopeG,
        weightBioscopeCollectingG,
        weightBioscopeMeasG,
        weightBioscopeTrendG,
        ...rest
    } = point;
    if (weightBioscopeG !== undefined || weightBioscopeCollectingG !== undefined) {
        return {
            ...rest,
            weightBioscopeG: weightBioscopeG ?? null,
            weightBioscopeCollectingG: weightBioscopeCollectingG ?? null,
        };
    }
    const estimate = weightBioscopeMeasG ?? null;
    const ready = selectionQuality === 'ready';
    return {
        ...rest,
        weightBioscopeG: ready ? (weightBioscopeTrendG ?? estimate) : null,
        weightBioscopeCollectingG: ready ? null : estimate,
    };
}

/**
 * An older replica only ever listed pens with a Bioscope assigned, and had no notion of
 * a failed service call, so those are the defaults that describe its pens truthfully.
 */
function mapFeedingPenToDomain(pen: FeedingPenResponse): FeedingPen {
    return {
        ...pen,
        historicalSince: pen.historicalSince ?? null,
        bioscopeInPeriod: pen.bioscopeInPeriod ?? true,
        feedingServiceUnavailable: pen.feedingServiceUnavailable ?? false,
        feedCoverage: pen.feedCoverage ?? null,
    };
}

/**
 * Without `availablePens` (an older replica) the selector falls back to the pens in the
 * response, which is what it listed before the field existed. Those are only the
 * requested pens, so the overview flags the list as derived and the selector takes it
 * only while every pen is selected.
 */
function penOptionsFromPens(pens: FeedingPen[]): FeedingPenOption[] {
    return pens.map((pen) => ({
        cageId: pen.cageId,
        cageName: pen.cageName,
        historicalSince: pen.historicalSince,
        currentBioscope: true,
        bioscopeInPeriod: pen.bioscopeInPeriod,
    }));
}

/**
 * Maps the overview response into the domain shape. Fields pass through unchanged
 * except `feeding.lastFetchedAt`, which the wire carries as an ISO string and consumers
 * read as a `Date`, the Feed & Weight fields a previous backend sent differently, and
 * the fields an older replica omits, which get the defaults that describe its response.
 */
export function mapFeedingDashboardOverviewToDomain(
    response: FeedingDashboardOverviewResponse
): FeedingDashboardOverview {
    const pens = response.pens.map(mapFeedingPenToDomain);
    return {
        ...response,
        siteToday: response.siteToday ?? null,
        feeding: mapFeedingRefreshStatusToDomain(response.feeding),
        feedWeightSeries: response.feedWeightSeries.map((point) =>
            mapSeriesPointToDomain(point, response.summary.bioscopeDataQuality)
        ),
        feedWeightOverlaySources:
            response.feedWeightOverlaySources ?? PREVIOUS_BACKEND_OVERLAY_SOURCES,
        summary: mapFeedingSummaryToDomain(response.summary, pens),
        pens,
        availablePens: response.availablePens ?? penOptionsFromPens(pens),
        availablePensDerived: response.availablePens === undefined,
    };
}
